/*
 * How a job repeats, in words, and the Repeats choices made from a visit's date (owner, Oct 31, like Jobber's Repeats list:
 * Daily, Weekly on Mondays, Every 2 weeks on Mondays, Monthly on the 21st, As needed, Custom).
 */
import { addDays, firstRepeat, isoDay, LAST_DAY, LAST_WEEK, type RepeatRule, type ScheduledJob } from "./schedule.js";

/** The repeat fields a job keeps. */
export interface RepeatValue {
  frequency: string;
  repeatDay: number | null;
  repeatRule: RepeatRule | null;
}

export const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** 1st, 2nd, 3rd, 4th, 21st. */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${String(n)}th`;
  return `${String(n)}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

const dayName = (d: number) => WEEKDAY_NAMES[(d - 1 + 7) % 7]!;
const days = (list: readonly number[]) => {
  const names = [...list].sort((a, b) => a - b).map((d) => `${dayName(d)}s`);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)!}` : (names[0] ?? "");
};
const listWords = (names: string[]) =>
  names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)!}` : (names[0] ?? "");
/** The week of the month a date is in: 1 to 4, or LAST_WEEK for days 29 to 31. */
export const weekOfMonth = (date: string) => Math.min(LAST_WEEK, Math.ceil(Number(date.slice(8, 10)) / 7));
const nthWords = (nth: number) => (nth >= LAST_WEEK ? "last" : ordinal(nth));
const dateWords = (d: number) => (d >= LAST_DAY ? "last day" : ordinal(d));
const often = (every: number, one: string, many: string) => (every <= 1 ? one : `Every ${String(every)} ${many}`);

function ruleWords(rule: RepeatRule, first: string): string {
  const every = Math.max(1, Math.floor(rule.every) || 1);
  switch (rule.unit) {
    case "day":
      return often(every, "Daily", "days");
    case "week":
      return `${often(every, "Weekly", "weeks")} on ${days(rule.weekdays?.length ? rule.weekdays : [isoDay(first)])}`;
    case "month": {
      const what = rule.monthWeekdays?.length
        ? listWords(rule.monthWeekdays.map((m) => `${nthWords(m.nth)} ${dayName(m.day)}`))
        : listWords((rule.monthDays?.length ? rule.monthDays : [Number(first.slice(8, 10))]).map(dateWords));
      return `${often(every, "Monthly", "months")} on the ${what}`;
    }
    case "year":
      return `${often(every, "Yearly", "years")} on ${MONTHS[Number(first.slice(5, 7)) - 1]!} ${String(Number(first.slice(8, 10)))}`;
  }
}

/** How a job repeats, in the words the screens use ("Weekly on Mondays", "Monthly on the 3rd Tuesday"). */
export function repeatWords(job: ScheduledJob): string {
  const first = firstRepeat(job) ?? job.startDate;
  const weekdayOf = () => job.repeatDay ?? (first ? isoDay(first) : 1);
  const nthDay = () => (first ? `the ${nthWords(weekOfMonth(first))} ${dayName(isoDay(first))}` : "");
  switch (job.frequency) {
    case "one-time":
      return "One time";
    case "as-needed":
      return "As needed";
    case "daily":
      return "Daily";
    case "weekly":
      return `Weekly on ${days([weekdayOf()])}`;
    case "biweekly":
      return `Every 2 weeks on ${days([weekdayOf()])}`;
    case "monthly":
      return first ? `Monthly on ${nthDay()}` : "Monthly";
    case "bimonthly":
      return first ? `Every 2 months on ${nthDay()}` : "Every 2 months";
    case "custom":
      return job.repeatRule && first ? ruleWords(job.repeatRule, first) : "Custom";
    default:
      return job.frequency;
  }
}

export interface RepeatChoice {
  key: string;
  label: string;
  value: RepeatValue;
}

/** The Repeats list for a visit on this date. Custom is the screen's own (it opens the custom repeat). */
export function repeatChoices(date: string): RepeatChoice[] {
  const day = isoDay(date);
  const nth = weekOfMonth(date);
  const one = (key: string, frequency: string, repeatDay: number | null = null, repeatRule: RepeatRule | null = null) => {
    const value = { frequency, repeatDay, repeatRule };
    return {
      key,
      value,
      label: frequency === "as-needed" ? "As needed, we won't prompt you" : repeatWords({ ...value, startDate: date }),
    };
  };
  return [
    one("daily", "daily"),
    one("weekly", "weekly", day),
    one("biweekly", "biweekly", day),
    one("monthly-date", "custom", null, { every: 1, unit: "month", monthDays: [Number(date.slice(8, 10))] }),
    one("monthly-day", "custom", null, { every: 1, unit: "month", monthWeekdays: [{ nth, day }] }),
    one("as-needed", "as-needed"),
  ];
}

/** Which of the Repeats choices a job's repeat is, or "custom". */
export function choiceOf(v: RepeatValue, date: string): string {
  const words = repeatWords({ ...v, startDate: date });
  return repeatChoices(date).find((c) => repeatWords({ ...c.value, startDate: date }) === words)?.key ?? "custom";
}

/**
 * The same repeat, moved onto a visit's new day: a weekly job moved from Monday to Tuesday repeats on Tuesdays from then
 * on. A repeat on several days (Mondays and Thursdays) keeps its days and only counts from the new date.
 */
export function moveRepeat(v: RepeatValue, date: string): RepeatValue {
  const day = isoDay(date);
  if (["weekly", "biweekly", "monthly", "bimonthly"].includes(v.frequency))
    return { ...v, repeatDay: v.repeatDay === null ? null : day };
  const r = v.repeatRule;
  if (v.frequency !== "custom" || !r) return v;
  if (r.unit === "week" && (r.weekdays?.length ?? 0) <= 1) return { ...v, repeatRule: { ...r, weekdays: [day] } };
  if (r.unit === "month" && r.monthWeekdays?.length === 1)
    return { ...v, repeatRule: { ...r, monthWeekdays: [{ nth: weekOfMonth(date), day }] } };
  if (r.unit === "month" && (r.monthDays?.length ?? 0) <= 1 && !r.monthWeekdays?.length)
    return { ...v, repeatRule: { ...r, monthDays: [Number(date.slice(8, 10))] } };
  return v;
}

/** The date n months after a date, on the month's last day when it is shorter (Jan 31 and 1 month is Feb 28). */
function addMonths(date: string, n: number): string {
  const m = Number(date.slice(0, 4)) * 12 + Number(date.slice(5, 7)) - 1 + n;
  const y = Math.floor(m / 12);
  const mo = (m % 12) + 1;
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  const d = Math.min(Number(date.slice(8, 10)), last);
  return `${String(y)}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * Switching how often a job runs (owner, Oct 31: "make the switching smarter and not rely on start dates"): the new repeat
 * counts from the last visit done, not the start date. Every 2 weeks to monthly with the last cut on Oct 24 gives Nov 21
 * (4 weeks on, same weekday). Never before today: an overdue job goes on today. No visit done yet, or only the day
 * changed (Mondays to Thursdays, same how often): today.
 */
export function nextAfterSwitch(v: RepeatValue, lastDone: string | null, today: string, was?: RepeatValue): string {
  if (!lastDone) return today;
  const r = v.repeatRule;
  const sameGap =
    was?.frequency === v.frequency &&
    (v.frequency !== "custom" || (was.repeatRule?.unit === r?.unit && was.repeatRule?.every === r?.every));
  if (sameGap) return today;
  const every = Math.max(1, Math.floor(r?.every ?? 1) || 1);
  let next: string | null = null;
  if (v.frequency === "daily") next = addDays(lastDone, 1);
  else if (v.frequency === "weekly") next = addDays(lastDone, 7);
  else if (v.frequency === "biweekly") next = addDays(lastDone, 14);
  else if (v.frequency === "monthly") next = addDays(lastDone, 28);
  else if (v.frequency === "bimonthly") next = addDays(lastDone, 56);
  else if (v.frequency === "custom" && r) {
    // A monthly repeat on a weekday (the 3rd Saturday) keeps the weekday: 4 weeks a month. On a date: the same date.
    if (r.unit === "day") next = addDays(lastDone, every);
    else if (r.unit === "week") next = addDays(lastDone, 7 * every);
    else if (r.unit === "month")
      next = r.monthDays?.length && !r.monthWeekdays?.length ? addMonths(lastDone, every) : addDays(lastDone, 28 * every);
    else next = addMonths(lastDone, 12 * every);
  }
  return next && next > today ? next : today;
}
